import { zodResolver } from "@hookform/resolvers/zod";
import {
  Activity,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import dashboardImg from "@/assets/alcatraz/dashboard.png";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSite } from "@/hooks/use-site";
import { alcatrazRequestBodySchema, type AlcatrazRequestIntent } from "@/lib/alcatraz-request";
import { localizeUrl } from "@/lib/site";

function createFormSchema(copy: {
  name: string;
  company: string;
  email: string;
  phone: string;
  consent: string;
}) {
  return alcatrazRequestBodySchema.omit({ intent: true }).extend({
    name: z.string().trim().min(2, copy.name).max(100, copy.name),
    company: z.string().trim().min(2, copy.company).max(150, copy.company),
    email: z.string().trim().email(copy.email).max(200, copy.email),
    phone: z.string().trim().min(5, copy.phone).max(50, copy.phone),
    requestDemo: z.boolean(),
    consent: z.boolean().refine((value) => value, copy.consent),
  });
}

type FormValues = z.input<ReturnType<typeof createFormSchema>>;

function safeEmail(value: string | null): string {
  if (!value) return "";
  const parsed = z.string().trim().email().max(200).safeParse(value);
  return parsed.success ? parsed.data : "";
}

function safeSource(value: string | null): string {
  if (!value) return "alcatraz-start-page";
  const parsed = z.string().trim().min(1).max(80).safeParse(value);
  return parsed.success ? parsed.data : "alcatraz-start-page";
}

function initiallyRequestsDemo(value: string | null): boolean {
  return value === "access_demo";
}

function initiallyNeedsSample(value: string | null): boolean {
  return value === "sample";
}

export function AlcatrazStartPage() {
  const { site, messages, href } = useSite();
  const formSchema = createFormSchema({
    name: messages.alcatrazRequestNameError,
    company: messages.alcatrazRequestCompanyError,
    email: messages.alcatrazRequestEmailError,
    phone: messages.alcatrazRequestPhoneError,
    consent: messages.contactConsentRequired,
  });
  const params = new URL(href, site.origin).searchParams;
  const initialEmail = safeEmail(params.get("email"));
  const source = safeSource(params.get("source"));
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [submittedWithDemo, setSubmittedWithDemo] = useState(false);
  const [submittedWithSample, setSubmittedWithSample] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      company: "",
      email: initialEmail,
      phone: "",
      requestDemo: initiallyRequestsDemo(params.get("intent")),
      needsSample: initiallyNeedsSample(params.get("want")),
      consent: false,
      source,
      website: "",
    },
  });
  const alcatrazHref = localizeUrl("/solutions/alcatraz-dms", site.locale, href);
  const privacyHref = localizeUrl("/privacy", site.locale, href);
  const endpoint = localizeUrl("/api/alcatraz-request", site.locale, href);
  const wantsDemo = form.watch("requestDemo");
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (status === "success") successHeadingRef.current?.focus();
  }, [status]);

  useEffect(() => {
    const current = new URL(window.location.href);
    if (!current.searchParams.has("email")) return;
    current.searchParams.delete("email");
    window.history.replaceState(
      window.history.state,
      "",
      `${current.pathname}${current.search}${current.hash}`,
    );
  }, []);

  const submit = form.handleSubmit(async (values) => {
    setStatus("idle");
    const { requestDemo, ...requestData } = values;
    const intent: AlcatrazRequestIntent = requestDemo ? "access_demo" : "access";
    setSubmittedWithDemo(requestDemo);
    setSubmittedWithSample(Boolean(values.needsSample));
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...requestData, intent }),
      });
      setStatus(response.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  });

  const benefits = [
    messages.alcatrazStartBenefit1,
    messages.alcatrazStartBenefit2,
    messages.alcatrazStartBenefit3,
  ];

  const steps = [
    messages.alcatrazStartStep1,
    messages.alcatrazStartStep2,
    messages.alcatrazStartStep3,
  ];

  return (
    <SiteLayout>
      <PageBreadcrumb
        items={[
          { label: messages.alcatrazBreadcrumbPage, href: alcatrazHref },
          { label: messages.alcatrazStartBreadcrumb },
        ]}
      />

      <section className="relative border-b border-border/60">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="bg-grid absolute inset-0 opacity-45" />
          <div
            className="absolute -right-48 top-0 h-[560px] w-[760px] rounded-full opacity-35 blur-3xl"
            style={{ background: "var(--gradient-aurora)" }}
          />
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-12 lg:grid-cols-[minmax(0,1fr)_minmax(420px,480px)] lg:items-start lg:gap-16 lg:py-16">
          <div className="lg:pt-2">
            <div className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {messages.alcatrazStartEyebrow}
            </div>
            <h1
              className="animate-fade-up mt-5 max-w-2xl text-balance text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl"
              style={{ animationDelay: "60ms" }}
            >
              {messages.alcatrazStartTitle}
            </h1>
            <p
              className="animate-fade-up mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground"
              style={{ animationDelay: "120ms" }}
            >
              {messages.alcatrazStartIntro}
            </p>

            <ul className="animate-fade-up mt-7 space-y-3" style={{ animationDelay: "180ms" }}>
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3 text-sm text-foreground/90">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>

            <div
              className="animate-fade-up mt-7 flex max-w-xl gap-3 rounded-xl border border-border/70 bg-card/60 p-4"
              style={{ animationDelay: "240ms" }}
            >
              <Activity className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {messages.alcatrazStartActiveTitle}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {messages.alcatrazStartActiveBody}
                </p>
              </div>
            </div>

            <div className="animate-fade-up mt-8" style={{ animationDelay: "300ms" }}>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {messages.alcatrazStartStepsTitle}
              </p>
              <ol className="mt-3 space-y-3">
                {steps.map((step, index) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-foreground/90">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                      {index + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <a
              className="animate-fade-up mt-7 inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/75 px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:border-primary/30 hover:text-primary"
              href={alcatrazHref}
              style={{ animationDelay: "360ms" }}
            >
              {messages.alcatrazStartFeaturesLink}
              <ArrowRight className="h-4 w-4" />
            </a>

            <figure
              className="animate-fade-up mt-8 hidden lg:block"
              style={{ animationDelay: "420ms" }}
            >
              <a
                aria-label={messages.alcatrazStartFeaturesLink}
                className="block overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xl shadow-foreground/[0.08] transition hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                href={alcatrazHref}
              >
                <div
                  aria-hidden="true"
                  className="flex items-center gap-1.5 border-b border-border/60 bg-muted/40 px-4 py-2.5"
                >
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                </div>
                <img
                  alt={messages.alcatrazStartDashboardAlt}
                  className="block aspect-[16/10] w-full object-cover object-top"
                  decoding="async"
                  loading="lazy"
                  src={dashboardImg}
                />
              </a>
            </figure>
          </div>

          <div
            className="animate-fade-up rounded-3xl border border-border/70 bg-card/95 p-6 shadow-xl shadow-foreground/[0.08] backdrop-blur sm:p-8 lg:sticky lg:top-28"
            style={{ animationDelay: "200ms" }}
          >
            {status === "success" ? (
              <div className="py-8 text-center" role="status">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <h2
                  className="mt-5 text-2xl font-semibold tracking-tight"
                  ref={successHeadingRef}
                  tabIndex={-1}
                >
                  {messages.alcatrazStartSuccessTitle}
                </h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                  {submittedWithDemo
                    ? messages.alcatrazStartDemoSuccessBody
                    : messages.alcatrazStartSuccessBody}
                </p>
                {submittedWithSample && (
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
                    {messages.alcatrazStartSampleSuccessNote}
                  </p>
                )}
                <p className="mx-auto mt-2 max-w-sm text-xs text-muted-foreground">
                  {messages.alcatrazStartSuccessTimeline}
                </p>
                <a
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/75 px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:border-primary/30 hover:text-primary"
                  href={alcatrazHref}
                >
                  {messages.alcatrazStartFeaturesLink}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold tracking-tight">
                  {messages.alcatrazStartFormTitle}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages.alcatrazStartFormIntro}
                </p>

                <form className="mt-6 space-y-5" noValidate onSubmit={submit}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label={messages.contactNameLabel}
                      error={form.formState.errors.name?.message}
                      errorId="alcatraz-start-name-error"
                    >
                      <Input
                        aria-describedby={
                          form.formState.errors.name ? "alcatraz-start-name-error" : undefined
                        }
                        aria-invalid={Boolean(form.formState.errors.name)}
                        autoComplete="name"
                        className="h-11 bg-background"
                        placeholder={messages.alcatrazRequestNamePlaceholder}
                        required
                        {...form.register("name")}
                      />
                    </Field>
                    <Field
                      label={messages.contactCompanyLabel}
                      error={form.formState.errors.company?.message}
                      errorId="alcatraz-start-company-error"
                    >
                      <Input
                        aria-describedby={
                          form.formState.errors.company ? "alcatraz-start-company-error" : undefined
                        }
                        aria-invalid={Boolean(form.formState.errors.company)}
                        autoComplete="organization"
                        className="h-11 bg-background"
                        placeholder={messages.alcatrazRequestCompanyPlaceholder}
                        required
                        {...form.register("company")}
                      />
                    </Field>
                    <Field
                      label={messages.contactEmailLabel}
                      error={form.formState.errors.email?.message}
                      errorId="alcatraz-start-email-error"
                    >
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          aria-describedby={
                            form.formState.errors.email ? "alcatraz-start-email-error" : undefined
                          }
                          aria-invalid={Boolean(form.formState.errors.email)}
                          autoComplete="email"
                          className="h-11 bg-background pl-9"
                          placeholder={messages.alcatrazRequestEmailPlaceholder}
                          required
                          type="email"
                          {...form.register("email")}
                        />
                      </div>
                    </Field>
                    <Field
                      label={messages.contactPhoneLabel}
                      error={form.formState.errors.phone?.message}
                      errorId="alcatraz-start-phone-error"
                    >
                      <div className="relative">
                        <Phone className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          aria-describedby={
                            form.formState.errors.phone ? "alcatraz-start-phone-error" : undefined
                          }
                          aria-invalid={Boolean(form.formState.errors.phone)}
                          autoComplete="tel"
                          className="h-11 bg-background pl-9"
                          inputMode="tel"
                          placeholder={messages.alcatrazRequestPhonePlaceholder}
                          required
                          type="tel"
                          {...form.register("phone")}
                        />
                      </div>
                    </Field>
                  </div>

                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/70 bg-background px-4 py-3.5 text-sm font-medium text-foreground transition hover:border-primary/30">
                    <input
                      className="peer sr-only"
                      type="checkbox"
                      {...form.register("requestDemo")}
                    />
                    <span
                      aria-hidden="true"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border border-border bg-background text-transparent transition peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span>{messages.alcatrazStartDemoOption}</span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/70 bg-background px-4 py-3.5 text-sm font-medium text-foreground transition hover:border-primary/30">
                    <input
                      className="peer sr-only"
                      type="checkbox"
                      {...form.register("needsSample")}
                    />
                    <span
                      aria-hidden="true"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border border-border bg-background text-transparent transition peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span>{messages.alcatrazStartSampleOption}</span>
                  </label>

                  <div
                    aria-hidden="true"
                    className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden"
                  >
                    <label htmlFor="alcatraz-start-website">
                      {messages.contactWebsiteHoneypotLabel}
                    </label>
                    <input
                      id="alcatraz-start-website"
                      autoComplete="off"
                      tabIndex={-1}
                      {...form.register("website")}
                    />
                  </div>
                  <input type="hidden" {...form.register("source")} />

                  <div>
                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-muted-foreground">
                      <input
                        aria-describedby={
                          form.formState.errors.consent ? "alcatraz-start-consent-error" : undefined
                        }
                        aria-invalid={Boolean(form.formState.errors.consent)}
                        className="peer sr-only"
                        required
                        type="checkbox"
                        {...form.register("consent")}
                      />
                      <span
                        aria-hidden="true"
                        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border border-border bg-background text-transparent transition peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span>
                        {messages.contactConsentPrefix}{" "}
                        <a
                          className="font-medium text-primary hover:underline"
                          href={privacyHref}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {messages.contactPrivacyLink}
                        </a>
                        .
                      </span>
                    </label>
                    {form.formState.errors.consent && (
                      <p
                        className="mt-2 text-xs font-medium text-destructive"
                        id="alcatraz-start-consent-error"
                      >
                        {messages.contactConsentRequired}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2.5 rounded-xl bg-primary/[0.05] px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <p>{messages.alcatrazStartEligibility}</p>
                  </div>

                  {status === "error" && (
                    <div
                      className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm"
                      role="alert"
                    >
                      <p className="font-medium text-foreground">{messages.contactFailureTitle}</p>
                      <p className="mt-1 text-muted-foreground">
                        {messages.contactFailureBody}{" "}
                        <a
                          className="font-medium text-primary hover:underline"
                          href="mailto:sales@inext.ltd"
                        >
                          sales@inext.ltd
                        </a>
                      </p>
                    </div>
                  )}

                  <Button
                    className="h-11 w-full"
                    disabled={form.formState.isSubmitting}
                    size="lg"
                    type="submit"
                  >
                    {form.formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {form.formState.isSubmitting
                      ? messages.alcatrazStartSubmitting
                      : wantsDemo
                        ? messages.alcatrazStartSubmitDemo
                        : messages.alcatrazStartSubmit}
                    {!form.formState.isSubmitting && <ArrowRight className="h-4 w-4" />}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    {messages.alcatrazStartNoCardNote}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Field({
  label,
  error,
  errorId,
  children,
}: {
  label: string;
  error?: string;
  errorId: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <span className="block text-sm font-medium text-foreground">
        {label}{" "}
        <span className="text-primary" aria-hidden="true">
          *
        </span>
      </span>
      {children}
      {error && (
        <span className="block text-xs font-medium text-destructive" id={errorId}>
          {error}
        </span>
      )}
    </label>
  );
}
